import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { WhiteLabelConfig, defaultConfig } from "@/types/whitelabel";

interface WhiteLabelContextType {
  config: WhiteLabelConfig;
  updateConfig: (newConfig: Partial<WhiteLabelConfig>) => void;
  resetConfig: () => void;
}

const WhiteLabelContext = createContext<WhiteLabelContextType | undefined>(undefined);

const STORAGE_KEY = "whitelabel-config";

export const WhiteLabelProvider = ({ children }: { children: ReactNode }) => {
  const [config, setConfig] = useState<WhiteLabelConfig>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : defaultConfig;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    applyTheme(config);
  }, [config]);

  const applyTheme = (config: WhiteLabelConfig) => {
    const root = document.documentElement;
    
    // Apply font
    root.style.setProperty("font-family", `'${config.font.family}', sans-serif`);
    
    // Update document title
    document.title = config.clinicName;
    
    // Update favicon
    const favicon = document.querySelector("link[rel='icon']") as HTMLLinkElement;
    if (favicon && config.favicon) {
      favicon.href = config.favicon;
    }
    
    // Load Google Font
    let fontLink = document.querySelector("link[data-whitelabel-font]") as HTMLLinkElement;
    if (!fontLink) {
      fontLink = document.createElement("link");
      fontLink.rel = "stylesheet";
      fontLink.setAttribute("data-whitelabel-font", "true");
      document.head.appendChild(fontLink);
    }
    fontLink.href = config.font.googleFontUrl;
  };

  const updateConfig = (newConfig: Partial<WhiteLabelConfig>) => {
    setConfig(prev => ({
      ...prev,
      ...newConfig,
      theme: { ...prev.theme, ...newConfig.theme },
      font: { ...prev.font, ...newConfig.font },
    }));
  };

  const resetConfig = () => {
    setConfig(defaultConfig);
  };

  return (
    <WhiteLabelContext.Provider value={{ config, updateConfig, resetConfig }}>
      {children}
    </WhiteLabelContext.Provider>
  );
};

export const useWhiteLabel = () => {
  const context = useContext(WhiteLabelContext);
  if (!context) {
    throw new Error("useWhiteLabel must be used within WhiteLabelProvider");
  }
  return context;
};
